// A restaurant's Orders page: one page with three tabs that are real addresses
// (/orders, /tables, /kitchen), so Back, reload and a kitchen tablet all keep
// their place. This file is the page around the tabs and the Orders tab itself
// (every open order with Print check and Checkout, the selected order beside
// the list, and today's paid orders); the Tables and Kitchen tabs are in
// restaurant.tsx. Plan: docs/plans/orders-page.md.
import { formatQty, mulDivRound, netOrderItems, orderTotals, requestExpired } from '@ims/core'
import type { OrderDetail, OrderRow, PaidOrder, PaymentMethod } from '@ims/data'
import {
  arrivalClass,
  Badge,
  Button,
  buttonClass,
  Card,
  cx,
  EmptyState,
  FilterPill,
  FilterPills,
  IconTile,
  Kbd,
  PageHeader,
  SearchInput,
  Segmented,
  SkeletonPage,
  SkeletonTable,
  type Area,
  useToast,
  Pagination,
} from '@ims/ui'
import {
  Bike,
  ChefHat,
  ClipboardCheck,
  type IconType,
  PicnicTable,
  Plus,
  Printer,
  ShoppingBag,
  Users,
  UtensilsCrossed,
} from '@ims/ui/icons'
import { type ReactNode, type Ref, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useLive, useNow, useSettings } from '../hooks.ts'
import { newIds } from '../lib/alerts.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { money, todayIso } from '../lib/format.ts'
import {
  clock,
  kitchenState,
  openFor,
  orderItemRows,
  orderName,
  sortOpenOrders,
  ticketByItem,
  unsentCount,
} from '../lib/orders.ts'
import { NewOrderMenu } from '../layout/new-order.tsx'
import { useListKeys } from '../lib/list-keys.ts'
import { usePaged } from '../lib/paging.ts'
import { useCan, useRepo } from '../workspace.tsx'
import { DeliveriesBoard, NewDeliveryDialog, useActiveDeliveries } from './deliveries.tsx'
import {
  type Floor,
  type Service,
  useFloor,
  useKitchenTickets,
  usePaidOrders,
  useService,
} from './floor.ts'
import { GuestRequests } from './guest-requests.tsx'
import { type OrderActionDeps, printGuestCheck, sendOrderToKitchen } from './order-actions.tsx'
import { useBuildContext, useWrite } from './order-hooks.ts'
import { looseOrders, NewTakeawayDialog, type OrderDialog, OrderDialogs } from './restaurant.tsx'
import { BillPrintMenu, openPrintMenu } from './print-menu.tsx'
import { ServerRow, useServerName } from './server-dialog.tsx'

type ServiceType = OrderRow['service_type']

const TABS: readonly { to: string; label: string; icon: IconType; about: string }[] = [
  { to: '/orders', label: 'Orders', icon: ClipboardCheck, about: '' },
  {
    to: '/tables',
    label: 'Tables',
    icon: PicnicTable,
    about: 'Tap a free table to start an order, or a busy one to add to it.',
  },
  {
    to: '/kitchen',
    label: 'Kitchen',
    icon: ChefHat,
    about: 'Tickets waiting to be made, oldest first. Start one, then mark it ready.',
  },
]

/** The facts under the page title, with a dot between them. */
function Summary({ parts }: { parts: ReactNode[] }) {
  const shown = parts.filter(Boolean)
  return (
    <span className="flex flex-wrap gap-x-1.5">
      {shown.map((part, i) => (
        <span key={i}>
          {part}
          {i < shown.length - 1 && <span className="ml-1.5 text-subtle">·</span>}
        </span>
      ))}
    </span>
  )
}

const strong = (text: ReactNode) => <b className="font-semibold text-ink">{text}</b>

/** The page around the three tabs: the title, guests' requests, the tabs with their counts. */
export function ServiceLayout() {
  const floor = useFloor()
  const tickets = useKitchenTickets()
  const settings = useSettings()
  const now = useNow()
  const canSetUp = useCan('manager')
  const { pathname } = useLocation()
  const [params] = useSearchParams()
  const tab = TABS.find((t) => pathname.startsWith(t.to)) ?? TABS[0]!
  const showPaid = tab.to === '/orders' && params.get('show') === 'paid'
  const paid = usePaidOrders(todayIso(), showPaid)

  const waiting = floor?.requests.filter((g) => !requestExpired(g.request, now.getTime())).length
  // The requests strip fades in when a request arrives while the page is open, never with
  // the page itself.
  const [wasEmpty, setWasEmpty] = useState(false)
  if (waiting === 0 && !wasEmpty) setWasEmpty(true)

  if (!floor) return <SkeletonPage label="Loading orders" />

  const tables = floor.tables.filter((t) => t.is_active)
  const counts: Record<string, number> = {
    '/orders': floor.orders.length,
    '/tables':
      tables.filter((t) => floor.orders.some((o) => o.order.table_id === t.id)).length +
      looseOrders(floor).length,
    '/kitchen': tickets?.length ?? 0,
  }
  const service: Service = { floor, tickets, paid }

  let summary: ReactNode = tab.about
  if (showPaid) {
    const kept = (paid ?? []).filter((p) => p.invoice.status === 'finalized')
    const last = kept[0]?.invoice.finalized_at
    summary = paid ? (
      <Summary
        parts={[
          strong(`${kept.length} paid today`),
          <>
            {strong(
              money(
                kept.reduce((sum, p) => sum + p.invoice.total_paisa, 0),
                settings,
              ),
            )}{' '}
            taken
          </>,
          last && `last one ${openFor(last, now)} ago`,
        ]}
      />
    ) : (
      'Loading today’s paid orders…'
    )
  } else if (tab.to === '/orders') {
    const billed = floor.orders.filter((o) => o.order.status === 'billed').length
    const ready = floor.orders.filter(
      (o) => o.order.status !== 'billed' && kitchenState(o.order, o.tickets).label === 'Ready',
    ).length
    const total = floor.orders.reduce(
      (sum, o) => sum + orderTotals(orderItemRows(o.items)).totalPaisa,
      0,
    )
    summary = (
      <Summary
        parts={[
          strong(`${floor.orders.length} open`),
          <>{strong(money(total, settings))} to bill</>,
          billed > 0 && <span className="font-semibold text-primary">{billed} waiting to pay</span>,
          ready > 0 && <span className="font-semibold text-success">{ready} ready</span>,
        ]}
      />
    )
  }

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle={summary}
        actions={
          // With no tables yet the Tables tab's empty state carries this button.
          tab.to === '/tables' &&
          canSetUp &&
          tables.length > 0 && (
            <Link to="/settings?tab=tables" className={buttonClass({ variant: 'secondary' })}>
              Set up tables
            </Link>
          )
        }
      />
      <GuestRequests requests={floor.requests} settings={settings} now={now} reveal={wasEmpty} />
      {/* The three tabs sit in one pill; on a phone they share its width. */}
      <nav
        aria-label="Orders, tables and kitchen"
        className="mb-4 grid grid-cols-3 gap-1 rounded-xl border border-border bg-surface p-1 sm:inline-grid sm:auto-cols-auto sm:grid-flow-col sm:grid-cols-none"
      >
        {TABS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              cx(
                'flex items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm transition-colors',
                isActive
                  ? 'bg-kitchen-light font-bold text-kitchen'
                  : 'font-semibold text-muted hover:bg-soft hover:text-ink',
              )
            }
          >
            <Icon className="size-[17px] max-sm:hidden" aria-hidden />
            {label}
            {counts[to]! > 0 && <span className="tabular">{counts[to]}</span>}
          </NavLink>
        ))}
      </nav>
      <Outlet context={service} />
    </div>
  )
}

// -----------------------------------------------------------------------------
// The Orders tab
// -----------------------------------------------------------------------------

type OrderType = 'all' | ServiceType

const TYPE_LABEL: Record<OrderType, string> = {
  all: 'All',
  dine_in: 'Dine in',
  takeaway: 'Takeaway',
  delivery: 'Delivery',
}

/** Each way of serving has its icon and colour, here and on the dashboard. */
export const TYPE_LOOK: Record<ServiceType, { icon: IconType; area: Area }> = {
  dine_in: { icon: UtensilsCrossed, area: 'kitchen' },
  takeaway: { icon: ShoppingBag, area: 'take' },
  delivery: { icon: Bike, area: 'insight' },
}

const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Cash',
  bank: 'Bank',
  card: 'Card',
  wallet: 'Wallet',
  cheque: 'Cheque',
  other: 'Other',
}

// One row of the list. From 640px it is a table row; below that the same cells make a
// card (name and total, the note and badge under them, the buttons across the bottom).
const COLS = 'sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1.15fr)_minmax(0,0.85fr)_minmax(0,0.8fr)_8rem]'
const ROW = cx(
  'grid items-center gap-x-3 gap-y-1 max-sm:grid-cols-[minmax(0,1fr)_auto] max-sm:rounded-xl max-sm:border max-sm:border-border max-sm:bg-surface max-sm:p-3 max-sm:shadow-card',
  'sm:border-b sm:border-border/60 sm:px-4 sm:py-2.5',
  COLS,
)
const CELL = {
  name: 'flex min-w-0 items-center gap-2.5 max-sm:col-start-1 max-sm:row-start-1',
  note: 'text-[13px] text-muted max-sm:col-start-1 max-sm:row-start-2 max-sm:pl-[38px] max-sm:text-xs',
  badge: 'max-sm:col-start-2 max-sm:row-start-2 max-sm:justify-self-end',
  total: 'tabular text-right font-bold max-sm:col-start-2 max-sm:row-start-1',
  actions:
    'flex justify-end gap-1.5 max-sm:col-span-2 max-sm:row-start-3 max-sm:mt-2 max-sm:gap-2 max-sm:empty:hidden',
}
// The buttons on a row: 34px in the table, a comfortable 40px across a phone card.
const ROW_BUTTON = 'h-[34px] text-[13px] max-sm:h-10 max-sm:flex-1'

/**
 * The orders that appeared since the list last changed, so only they ease in. What is
 * already open when the tab is opened is the baseline and does not move.
 */
function useArrived(ids: readonly string[]): ReadonlySet<string> {
  const [seen, setSeen] = useState(ids)
  const [arrived, setArrived] = useState<ReadonlySet<string>>(new Set())
  if (ids.join() !== seen.join()) {
    setArrived(new Set(newIds(new Set(seen), ids)))
    setSeen(ids)
  }
  return arrived
}

/** The side panel shows from 1024px; below that a row opens the order itself. */
const wide = () => window.matchMedia('(min-width: 64rem)').matches

function ListHead({ columns }: { columns: readonly string[] }) {
  return (
    <div
      aria-hidden
      className={cx(
        'grid gap-x-3 border-b border-border px-4 py-2.5 text-table-head text-subtle max-sm:hidden',
        COLS,
      )}
    >
      {columns.map((c) => (
        <span key={c} className={cx(c === 'Total' && 'text-right')}>
          {c}
        </span>
      ))}
      <span />
    </div>
  )
}

/** The frame of a list: a card from 640px, a stack of cards on a phone. */
function ListCard({
  label,
  head,
  foot,
  pager,
  ref,
  children,
}: {
  label: string
  head: readonly string[]
  foot: ReactNode
  /** The pages under the list (25 a page). */
  pager?: ReactNode
  ref?: Ref<HTMLElement>
  children: ReactNode
}) {
  return (
    <section
      ref={ref}
      aria-label={label}
      className="min-w-0 sm:overflow-hidden sm:rounded-card sm:bg-surface sm:shadow-card"
    >
      <ListHead columns={head} />
      <ul className="max-sm:space-y-2.5">{children}</ul>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
        {foot}
      </div>
      {pager}
    </section>
  )
}

const selectedRow = 'lg:bg-sell-wash lg:shadow-[inset_3px_0_0_var(--color-primary)]'

/** Every open order, or today's paid ones (`?show=paid`), filtered by type (`?type=`). */
export function OpenOrdersScreen() {
  const { floor, paid } = useService()
  const settings = useSettings()
  const [params, setParams] = useSearchParams()
  const deliveries = useActiveDeliveries()
  const [search, setSearch] = useState('')
  const canServe = useCan('waiter')
  const show = params.get('show') === 'paid' ? 'paid' : 'open'

  const out = (deliveries ?? []).filter((d) => d.delivery.status !== 'pending')
  // The Delivery pill shows once delivery is on, or while any delivery is still going.
  const deliveryOn = !!settings?.delivery_product_id
  const types: OrderType[] = ['all', 'dine_in', 'takeaway']
  if (
    deliveryOn ||
    out.length > 0 ||
    floor.orders.some((o) => o.order.service_type === 'delivery') ||
    paid?.some((p) => p.order.service_type === 'delivery')
  )
    types.push('delivery')
  const asked = params.get('type') as OrderType | null
  const type: OrderType = asked && types.includes(asked) ? asked : 'all'
  const go = (next: { show?: 'open' | 'paid'; type?: OrderType }) => {
    const s = next.show ?? show
    const t = next.type ?? type
    setParams(
      { ...(s === 'paid' ? { show: 'paid' } : {}), ...(t === 'all' ? {} : { type: t }) },
      { replace: true },
    )
  }
  const typed = show === 'paid' ? (paid ?? []) : floor.orders
  const count = (t: OrderType) =>
    t === 'all' ? typed.length : typed.filter((o) => o.order.service_type === t).length

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <Segmented
          label="Open or paid"
          className="max-sm:w-full"
          value={show}
          onChange={(s) => go({ show: s })}
          options={[
            { value: 'open', label: `Open ${floor.orders.length}` },
            { value: 'paid', label: paid ? `Paid today ${paid.length}` : 'Paid today' },
          ]}
        />
        <SearchInput
          listKeys
          value={search}
          onChange={setSearch}
          placeholder={
            show === 'paid' ? 'Table, name or bill number' : 'Table, name or order number'
          }
          className="ml-auto w-64 max-sm:hidden"
        />
      </div>
      <FilterPills label="Order type" className="mb-4">
        {types.map((t) => {
          const look = t === 'all' ? null : TYPE_LOOK[t]
          const Icon = look?.icon
          return (
            <FilterPill
              key={t}
              selected={type === t}
              onSelect={() => go({ type: t })}
              area={look?.area}
              icon={Icon && <Icon className="size-4" aria-hidden />}
              count={count(t)}
              attention={
                show === 'open' &&
                t !== 'all' &&
                t !== 'dine_in' &&
                floor.orders.some(
                  (o) =>
                    o.order.service_type === t &&
                    kitchenState(o.order, o.tickets).label === 'Ready',
                )
                  ? 'ready'
                  : undefined
              }
            >
              {TYPE_LABEL[t]}
            </FilterPill>
          )
        })}
      </FilterPills>
      {show === 'paid' ? (
        <PaidList paid={paid} type={type} search={search} />
      ) : (
        <OpenList floor={floor} type={type} search={search} deliveryOn={deliveryOn} />
      )}
      {show === 'open' && type === 'delivery' && (
        <DeliveriesBoard details={deliveries} canServe={canServe} />
      )}
    </div>
  )
}

function useOrderActionDeps(): OrderActionDeps & { busy: boolean } {
  const repo = useRepo()
  const settings = useSettings()
  const toast = useToast()
  const context = useBuildContext()
  const { write, busy } = useWrite()
  return { repo, settings, toast, context, write, busy }
}

const matches = (search: string, ...texts: (string | null | undefined)[]) => {
  const q = search.trim().toLowerCase()
  return !q || texts.some((t) => t?.toLowerCase().includes(q))
}

function OpenList({
  floor,
  type,
  search,
  deliveryOn,
}: {
  floor: Floor
  type: OrderType
  search: string
  deliveryOn: boolean
}) {
  const settings = useSettings()
  const now = useNow()
  const navigate = useNavigate()
  const openItem = useOpen()
  const canServe = useCan('waiter')
  const actions = useOrderActionDeps()
  const [picked, setPicked] = useState<string | null>(null)
  const [starting, setStarting] = useState<'takeaway' | 'delivery' | null>(null)
  const arrived = useArrived(floor.orders.map((o) => o.order.id))

  const table = (order: OrderRow) => floor.tables.find((t) => t.id === order.table_id)
  const nameOf = (order: OrderRow) => orderName(order, table(order)?.name ?? null)
  /** What the kitchen and the guest check call the order. */
  const whereOf = (order: OrderRow) =>
    order.service_type === 'dine_in' ? (table(order)?.name ?? null) : orderName(order, null)
  const ordered = sortOpenOrders(
    floor.orders.filter(
      (o) => (type === 'all' || o.order.service_type === type) && matches(search, nameOf(o.order)),
    ),
  )
  // 25 a page; the keys move within the page.
  const paged = usePaged(ordered, JSON.stringify([type, search]))
  const shown = paged.rows
  const selected = shown.find((o) => o.order.id === picked) ?? shown[0]
  const linesOf = (o: OrderDetail) => netOrderItems(orderItemRows(o.items)).length
  const billable = (o: OrderDetail | undefined): o is OrderDetail =>
    !!o && canServe && linesOf(o) > 0
  const print = (o: OrderDetail) => void printGuestCheck(actions, o, whereOf(o.order))
  const checkout = (o: OrderDetail) => navigate(`/bill?order=${o.order.id}`)

  const listRef = useRef<HTMLElement>(null)
  useListKeys({
    ids: shown.map((o) => o.order.id),
    active: selected?.order.id ?? null,
    setActive: setPicked,
    page: paged,
    list: listRef,
    open: () => selected && navigate(`/tables/${selected.order.id}`),
    keys: {
      p: () => billable(selected) && print(selected),
      c: () => billable(selected) && checkout(selected),
    },
  })

  if (shown.length === 0) {
    const none =
      search.trim() !== ''
        ? {
            icon: ClipboardCheck,
            title: 'No order matches',
            text: 'Try a table, a name or a number.',
          }
        : type === 'takeaway'
          ? {
              icon: ShoppingBag,
              title: 'No takeaway orders',
              text: 'A takeaway goes to the kitchen like a table and is paid when it is picked up.',
            }
          : type === 'delivery'
            ? {
                icon: Bike,
                title: 'No deliveries in the kitchen',
                text: 'A delivery has a customer and an address, goes to the kitchen, and leaves with its bill and rider.',
              }
            : {
                icon: ClipboardCheck,
                title: type === 'all' ? 'No open orders' : 'No dine-in orders',
                text: 'An order shows here from the moment it is started until it is paid.',
              }
    const Icon = none.icon
    return (
      <>
        <Card className="p-0">
          <EmptyState
            icon={<Icon className="size-6" />}
            title={none.title}
            description={none.text}
            action={
              search.trim() !== '' ? undefined : type === 'takeaway' ? (
                canServe && (
                  <Button
                    icon={<Plus className="size-4" />}
                    onClick={() => setStarting('takeaway')}
                  >
                    New takeaway
                  </Button>
                )
              ) : type === 'delivery' ? (
                canServe &&
                deliveryOn && (
                  <Button
                    icon={<Plus className="size-4" />}
                    onClick={() => setStarting('delivery')}
                  >
                    New delivery
                  </Button>
                )
              ) : type === 'dine_in' ? (
                <Link to="/tables" className={buttonClass({ variant: 'secondary' })}>
                  Go to tables
                </Link>
              ) : (
                // The same three choices as "New order" in the top bar.
                <NewOrderMenu
                  label="Add new order"
                  align="start"
                  triggerClassName={buttonClass()}
                  trigger={
                    <>
                      <Plus className="size-4" aria-hidden />
                      Add new order
                    </>
                  }
                />
              )
            }
          />
        </Card>
        {starting === 'takeaway' && <NewTakeawayDialog onClose={() => setStarting(null)} />}
        {starting === 'delivery' && <NewDeliveryDialog onClose={() => setStarting(null)} />}
      </>
    )
  }

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_372px] lg:items-start lg:gap-4">
      <ListCard
        ref={listRef}
        label="Open orders"
        head={['Order', 'Items', 'Kitchen', 'Total']}
        pager={
          <Pagination
            offset={paged.offset}
            limit={paged.limit}
            total={paged.total}
            onChange={paged.setOffset}
          />
        }
        foot={
          <>
            <span>Waiting to pay first, then ready, then the oldest.</span>
            <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 max-lg:hidden">
              <span>
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move
              </span>
              <span>
                <Kbd>Enter</Kbd> open
              </span>
              <span>
                <Kbd>P</Kbd> print check
              </span>
              <span>
                <Kbd>C</Kbd> checkout
              </span>
            </span>
          </>
        }
      >
        {shown.map((o) => {
          const { order } = o
          const { icon: Icon, area } = TYPE_LOOK[order.service_type]
          const state = kitchenState(order, o.tickets)
          const lines = linesOf(o)
          const unsent = unsentCount(o)
          const name = nameOf(order)
          const on = selected?.order.id === order.id
          return (
            <li
              key={order.id}
              aria-current={on ? 'true' : undefined}
              aria-label={`${name}, ${state.label.toLowerCase()}`}
              onClick={() => (wide() ? setPicked(order.id) : openItem(`/tables/${order.id}`))}
              className={cx(
                ROW,
                'cursor-pointer',
                on ? selectedRow : 'lg:hover:bg-soft',
                arrived.has(order.id) && arrivalClass,
              )}
            >
              <span className={CELL.name}>
                <IconTile area={area} size="sm">
                  <Icon className="size-[17px]" aria-hidden />
                </IconTile>
                <span className="min-w-0">
                  <Link
                    to={`/tables/${order.id}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      moveClick(`/tables/${order.id}`, 'open')(e)
                    }}
                    className="block truncate font-bold hover:underline focus-visible:underline"
                  >
                    {name}
                  </Link>
                  <span className="block text-xs text-muted">
                    {order.service_type === 'dine_in' &&
                      `${order.covers} ${order.covers === 1 ? 'guest' : 'guests'} · `}
                    {openFor(order.opened_at, now)}
                  </span>
                </span>
              </span>
              <span className={CELL.note}>
                {lines === 0 ? 'Nothing ordered yet' : `${lines} ${lines === 1 ? 'item' : 'items'}`}
                {unsent > 0 && (
                  <span className="font-semibold text-warning"> · {unsent} not sent</span>
                )}
              </span>
              <span className={CELL.badge}>
                <Badge tone={state.tone}>{state.label}</Badge>
              </span>
              <span className={CELL.total}>
                {money(orderTotals(orderItemRows(o.items)).totalPaisa, settings)}
              </span>
              <span className={CELL.actions} onClick={(e) => e.stopPropagation()}>
                {billable(o) && (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={`Print check for ${name}`}
                      title="Print check"
                      className={cx(ROW_BUTTON, 'sm:w-[34px] sm:px-0')}
                      icon={<Printer className="size-4" />}
                      onClick={() => print(o)}
                    >
                      <span className="sm:hidden">Print check</span>
                    </Button>
                    <Link
                      to={`/bill?order=${order.id}`}
                      aria-label={`${order.service_type === 'delivery' ? 'Send out' : 'Checkout'} ${name}`}
                      className={buttonClass({
                        variant: 'soft',
                        size: 'sm',
                        className: cx(ROW_BUTTON, 'px-3.5'),
                      })}
                    >
                      {order.service_type === 'delivery' ? 'Send out' : 'Checkout'}
                    </Link>
                  </>
                )}
              </span>
            </li>
          )
        })}
      </ListCard>
      {selected && (
        <OrderPeek
          key={selected.order.id}
          detail={selected}
          floor={floor}
          actions={actions}
          canServe={canServe}
        />
      )}
    </div>
  )
}

// The frame both side panels share: beside the list from 1024px, hidden below.
const PEEK = 'p-0 max-lg:hidden lg:sticky lg:top-0'

/** The selected open order beside the list: what is on it, and the buttons to bill it. */
function OrderPeek({
  detail,
  floor,
  actions,
  canServe,
}: {
  detail: OrderDetail
  floor: Floor
  actions: OrderActionDeps & { busy: boolean }
  canServe: boolean
}) {
  const settings = useSettings()
  const now = useNow()
  const [dialog, setDialog] = useState<OrderDialog | null>(null)
  const { order } = detail
  const items = orderItemRows(detail.items)
  const left = netOrderItems(items)
  const table = floor.tables.find((t) => t.id === order.table_id)
  const area = floor.areas.find((a) => a.id === table?.area_id)
  const dineIn = order.service_type === 'dine_in'
  const where = dineIn ? (table?.name ?? null) : orderName(order, null)
  const { icon: Icon, area: colour } = TYPE_LOOK[order.service_type]
  const state = kitchenState(order, detail.tickets)
  const unsent = unsentCount(detail)
  const ticketOf = ticketByItem(detail.tickets, detail.sent)
  const units = left.reduce((sum, n) => sum + n.qtyMilli, 0)
  const sendOut = order.service_type === 'delivery'

  return (
    <Card className={PEEK} aria-label={`${where ?? 'Order'}, selected order`} role="complementary">
      <div className="border-b border-border px-4 pt-4 pb-3">
        <div className="flex items-center gap-2.5">
          <IconTile area={colour}>
            <Icon className="size-5" aria-hidden />
          </IconTile>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg leading-tight font-bold">
              {where ?? 'No table'}
              {area && <span className="text-sm font-medium text-muted"> · {area.name}</span>}
            </h2>
            <p className="flex items-center gap-1.5 text-xs text-muted">
              {dineIn && (
                <>
                  <Users className="size-[15px]" aria-hidden />
                  {order.covers} {order.covers === 1 ? 'guest' : 'guests'} ·
                </>
              )}{' '}
              open {openFor(order.opened_at, now)}
            </p>
          </div>
          <Badge tone={state.tone}>{state.label}</Badge>
        </div>
        <ServerRow
          serverId={order.server_id}
          canChange={canServe}
          onChange={() => setDialog('server')}
        />
        {canServe && (
          <div className="-mx-2 mt-2 flex flex-wrap gap-x-1 whitespace-nowrap">
            {dineIn && (
              <Button size="sm" variant="ghost" onClick={() => setDialog('move')}>
                Move
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setDialog('merge')}>
              Merge
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={units <= 1000}
              onClick={() => setDialog('split')}
            >
              Split
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:text-danger"
              onClick={() => setDialog('void')}
            >
              Cancel order
            </Button>
          </div>
        )}
      </div>
      {left.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed className="size-6" />}
          title="Nothing ordered yet"
          description="Open the order to add a dish or a drink."
        />
      ) : (
        <ul className="divide-y divide-border/60 px-4">
          {left.map((n) => {
            const ticket = ticketOf.get(n.item.id)
            return (
              <li key={n.item.id} className="flex items-baseline gap-2.5 py-2.5">
                <span className="tabular w-8 shrink-0 font-bold">{formatQty(n.qtyMilli)}×</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{n.item.description}</span>
                  {n.item.note && <span className="block text-xs italic">{n.item.note}</span>}
                  {n.item.station !== 'none' &&
                    (!ticket ? (
                      <span className="block text-xs font-semibold text-warning">Not sent yet</span>
                    ) : ticket.status === 'ready' ? (
                      <span className="block text-xs font-semibold text-success">Ready</span>
                    ) : ticket.status === 'preparing' ? (
                      <span className="block text-xs text-muted">Preparing</span>
                    ) : (
                      <span className="block text-xs text-muted">Sent {clock(ticket.sent_at)}</span>
                    ))}
                </span>
                <span className="tabular font-semibold">
                  {money(mulDivRound(n.item.unit_price_paisa, n.qtyMilli, 1000), settings)}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {canServe && unsent > 0 && (
        <div className="mx-4 mt-3 flex items-center gap-2.5 rounded-xl border border-kitchen/30 bg-kitchen-wash px-3 py-2 text-[13px]">
          <span className="flex-1 font-semibold text-warning">
            {unsent === 1
              ? '1 item is not sent to the kitchen.'
              : `${unsent} items are not sent to the kitchen.`}
          </span>
          <Button
            size="sm"
            variant="secondary"
            loading={actions.busy}
            onClick={() => void sendOrderToKitchen(actions, detail, where)}
          >
            Send ({unsent})
          </Button>
        </div>
      )}
      <div className="mt-3 space-y-3 border-t border-border p-4">
        <div className="flex items-baseline justify-between">
          <span className="font-bold">Total</span>
          <output aria-label="Selected order total" className="text-metric">
            {money(orderTotals(items).totalPaisa, settings)}
          </output>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="lg"
            variant="secondary"
            icon={<Printer className="size-4" />}
            disabled={left.length === 0 || !canServe}
            onClick={() => void printGuestCheck(actions, detail, where)}
          >
            Print check
          </Button>
          {left.length === 0 || !canServe ? (
            <Button size="lg" disabled>
              {sendOut ? 'Send out' : 'Checkout'}
            </Button>
          ) : (
            <Link to={`/bill?order=${order.id}`} className={buttonClass({ size: 'lg' })}>
              {sendOut ? 'Send out' : 'Checkout'}
            </Link>
          )}
        </div>
        <Link
          to={`/tables/${order.id}`}
          className="block text-center text-[13px] font-semibold text-primary hover:underline"
        >
          Open the order to add dishes →
        </Link>
      </div>
      <OrderDialogs
        detail={detail}
        floor={floor}
        dialog={dialog}
        onClose={() => setDialog(null)}
        inList
      />
    </Card>
  )
}

// -----------------------------------------------------------------------------
// Paid today
// -----------------------------------------------------------------------------

function PaidBadge({ paid }: { paid: PaidOrder }) {
  if (paid.invoice.status === 'cancelled') return <Badge tone="danger">Cancelled</Badge>
  // Nothing received yet: a delivery paid in cash at the door.
  if (paid.methods.length === 0) return <Badge tone="warning">Not paid yet</Badge>
  return <Badge tone="success">{paid.methods.map((m) => METHOD_LABEL[m]).join(' + ')}</Badge>
}

function PaidList({
  paid,
  type,
  search,
}: {
  paid: PaidOrder[] | undefined
  type: OrderType
  search: string
}) {
  const settings = useSettings()
  const serverName = useServerName()
  const navigate = useNavigate()
  const openItem = useOpen()
  const [picked, setPicked] = useState<string | null>(null)
  const all = (paid ?? []).filter(
    (p) =>
      (type === 'all' || p.order.service_type === type) &&
      matches(search, orderName(p.order, p.tableName), p.invoice.number),
  )
  const paged = usePaged(all, JSON.stringify([type, search]))
  const shown = paged.rows
  const selected = shown.find((p) => p.order.id === picked) ?? shown[0]
  const printable = (p: PaidOrder | undefined): p is PaidOrder => p?.invoice.status === 'finalized'

  const listRef = useRef<HTMLElement>(null)
  useListKeys({
    ids: shown.map((p) => p.order.id),
    active: selected?.order.id ?? null,
    setActive: setPicked,
    page: paged,
    list: listRef,
    open: () => selected && navigate(`/invoices/${selected.invoice.id}`),
    keys: { p: () => printable(selected) && openPrintMenu(selected.invoice.id) },
  })

  if (!paid)
    return (
      <Card className="p-0">
        <SkeletonTable />
      </Card>
    )
  if (shown.length === 0)
    return (
      <Card className="p-0">
        <EmptyState
          icon={<ClipboardCheck className="size-6" />}
          title={search.trim() ? 'No paid order matches' : 'Nothing paid yet today'}
          description="An order moves here when it is checked out. Older bills are in Invoices."
          action={
            <Link to="/invoices" className={buttonClass({ variant: 'secondary' })}>
              Open invoices
            </Link>
          }
        />
      </Card>
    )

  return (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_372px] lg:items-start lg:gap-4">
      <ListCard
        ref={listRef}
        label="Paid today"
        head={['Order', 'Bill', 'Paid by', 'Total']}
        pager={
          <Pagination
            offset={paged.offset}
            limit={paged.limit}
            total={paged.total}
            onChange={paged.setOffset}
          />
        }
        foot={
          <>
            <span>Newest first.</span>
            <Link to="/invoices" className="ml-auto hover:text-ink hover:underline">
              Older bills are in Invoices.
            </Link>
          </>
        }
      >
        {shown.map((p) => {
          const { icon: Icon, area } = TYPE_LOOK[p.order.service_type]
          const name = orderName(p.order, p.tableName)
          const on = selected?.order.id === p.order.id
          return (
            <li
              key={p.order.id}
              aria-current={on ? 'true' : undefined}
              aria-label={`${name}, ${p.invoice.number ?? 'bill'}`}
              onClick={() =>
                wide() ? setPicked(p.order.id) : openItem(`/invoices/${p.invoice.id}`)
              }
              className={cx(ROW, 'cursor-pointer', on ? selectedRow : 'lg:hover:bg-soft')}
            >
              <span className={CELL.name}>
                <IconTile area={area} size="sm">
                  <Icon className="size-[17px]" aria-hidden />
                </IconTile>
                <span className="min-w-0">
                  <span className="block truncate font-bold">{name}</span>
                  <span className="block text-xs text-muted">
                    Paid {p.invoice.finalized_at ? clock(p.invoice.finalized_at) : ''}
                  </span>
                </span>
              </span>
              <span className={CELL.note}>
                <Link
                  to={`/invoices/${p.invoice.id}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    moveClick(`/invoices/${p.invoice.id}`, 'open')(e)
                  }}
                  title={p.invoice.number ?? undefined}
                  className="block truncate font-semibold text-primary hover:underline"
                >
                  {p.invoice.number ?? 'Not numbered yet'}
                </Link>
              </span>
              <span className={CELL.badge}>
                <PaidBadge paid={p} />
              </span>
              <span className={CELL.total}>{money(p.invoice.total_paisa, settings)}</span>
              <span className={CELL.actions} onClick={(e) => e.stopPropagation()}>
                {printable(p) && (
                  <BillPrintMenu
                    invoiceId={p.invoice.id}
                    serverName={serverName}
                    label={`Reprint the bill of ${name}`}
                    trigger={
                      <>
                        <Printer className="size-4" aria-hidden />
                        Reprint
                      </>
                    }
                    triggerClassName={buttonClass({
                      variant: 'secondary',
                      size: 'sm',
                      className: cx(ROW_BUTTON, 'px-3'),
                    })}
                    className="max-sm:flex-1"
                  />
                )}
              </span>
            </li>
          )
        })}
      </ListCard>
      {selected && <PaidPeek key={selected.order.id} paid={selected} />}
    </div>
  )
}

/** The selected paid order beside the list: the bill as it was made, to reprint or open. */
function PaidPeek({ paid }: { paid: PaidOrder }) {
  const settings = useSettings()
  const serverName = useServerName()
  const detail = useLive(
    (r) => r.invoices.detail(paid.invoice.id),
    [paid.invoice.id],
    ['sales_invoices', 'sales_invoice_lines'],
  ).data
  const { icon: Icon, area } = TYPE_LOOK[paid.order.service_type]
  const name = paid.order.service_type === 'dine_in' ? paid.tableName : orderName(paid.order, null)
  const line = (label: string, value: ReactNode) => (
    <div className="flex justify-between gap-3 py-0.5 text-[13.5px] text-muted">
      <span>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  )
  return (
    <Card className={PEEK} aria-label={`${name ?? 'Order'}, paid order`} role="complementary">
      <div className="flex items-center gap-2.5 border-b border-border px-4 pt-4 pb-3">
        <IconTile area={area}>
          <Icon className="size-5" aria-hidden />
        </IconTile>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg leading-tight font-bold">{name ?? 'No table'}</h2>
          <p className="text-xs text-muted">
            {paid.invoice.number ?? 'Not numbered yet'}
            {paid.invoice.finalized_at && ` · paid ${clock(paid.invoice.finalized_at)}`}
          </p>
        </div>
        <PaidBadge paid={paid} />
      </div>
      {!detail ? (
        <SkeletonTable />
      ) : (
        <>
          <ul className="divide-y divide-border/60 px-4">
            {detail.lines.map((l) => (
              <li key={l.id} className="flex items-baseline gap-2.5 py-2.5">
                <span className="tabular w-8 shrink-0 font-bold">{formatQty(l.qty_milli)}×</span>
                <span className="min-w-0 flex-1 truncate font-semibold">{l.description}</span>
                <span className="tabular font-semibold">{money(l.total_paisa, settings)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 border-t border-border p-4">
            {line('Before VAT', money(detail.invoice.taxable_paisa, settings))}
            {line('VAT', money(detail.invoice.tax_paisa, settings))}
            {detail.invoice.discount_paisa > 0 &&
              line('Discount', money(detail.invoice.discount_paisa, settings))}
            <div className="mt-2 mb-1 flex items-baseline justify-between">
              <span className="font-bold">Total</span>
              <output aria-label="Selected bill total" className="text-metric">
                {money(detail.invoice.total_paisa, settings)}
              </output>
            </div>
            {line(
              'Paid by',
              paid.methods.length > 0
                ? paid.methods.map((m) => METHOD_LABEL[m]).join(' + ')
                : 'Not paid yet',
            )}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {paid.invoice.status === 'finalized' ? (
                <BillPrintMenu
                  invoiceId={paid.invoice.id}
                  serverName={serverName}
                  label="Reprint bill"
                  trigger={
                    <>
                      <Printer className="size-4" aria-hidden />
                      Reprint bill
                    </>
                  }
                  triggerClassName={buttonClass({
                    size: 'lg',
                    variant: 'secondary',
                    className: 'w-full',
                  })}
                />
              ) : (
                <Button
                  size="lg"
                  variant="secondary"
                  icon={<Printer className="size-4" />}
                  disabled
                >
                  Reprint bill
                </Button>
              )}
              <Link
                to={`/invoices/${paid.invoice.id}`}
                className={buttonClass({ size: 'lg', variant: 'secondary' })}
              >
                Open invoice
              </Link>
            </div>
            <p className="mt-3 text-xs text-muted">
              A paid bill never changes here. A mistake is fixed with a credit note from the
              invoice.
            </p>
          </div>
        </>
      )}
    </Card>
  )
}
